import { Alert, Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { useRetryTopup, useTopupsNeedingAttention } from "@/hooks/useTopups";
import type { TopupAttentionRow } from "@/interfaces/topups";
import { formatDateTime } from "@/utils/date-format";
import { fundingErrorMessage } from "@/utils/fundingError-helper";

/**
 * Campaigns whose card has started failing — the one thing on this page that is not an approval.
 *
 * DELIBERATELY NOT IN THE APPROVAL QUEUE. Collecting money a client already owes is not a
 * maker-checker decision: nobody needs a second signature to retry a card, and putting it through
 * the registry would make every row wait for an approver who has nothing to decide.
 *
 * It appears above the tabs and disappears when there is nothing wrong, because it is an alarm
 * rather than a list — a person should never have to go looking for it, and an empty box teaches
 * them to stop looking.
 */

const fmt = (credits: string) => `${Number(credits).toLocaleString()} credits`;

function AttentionRow({ row }: { row: TopupAttentionRow }) {
  const navigate = useNavigate();
  const { isGfg, organizationId } = usePermissions();
  const retry = useRetryTopup();
  const [result, setResult] = useState<string | null>(null);

  // Same organisation, not merely "can see the row": GFG staff hold unrestricted scope, so seeing
  // the campaign says nothing about whose cards these are.
  // GFG FIRST, then the organisation match. Staff choosing which of a client's cards gets charged
  // is not theirs to decide at all, so it is refused on who they are rather than on a comparison
  // that a null organisation on either side could quietly pass.
  const ownOrganisation = !isGfg && organizationId != null && organizationId === row.organizationId;

  async function onRetry() {
    setResult(null);
    try {
      const call = await retry.mutateAsync(row.campaignId);
      // Three outcomes and they are genuinely different: it worked, nothing was owed, or the card
      // said no again. A single "done" would send someone away believing a dead card was fixed.
      setResult(
        call.ok
          ? `Charged ${call.credits ?? "0"} credits.`
          : call.failure
            ? `Declined again: ${call.failure.message}`
            : "Nothing was owed — this campaign is above its margin.",
      );
    } catch (err) {
      // The server's own words. "Could not be raised" hid a 403, a call already in flight and a
      // genuine outage behind one sentence, which is exactly what made this undebuggable.
      setResult(fundingErrorMessage(err, "The retry could not be raised."));
    }
  }

  return (
    <Box
      sx={{
        border: "1px solid var(--color-card-border)",
        // Stopped is the one worth pausing on: nothing further happens on its own.
        // `--color-error` is not a token in this app, so this fell back to a red that appears
        // nowhere else in it. Good Wine for a campaign that has stopped collecting, its lightest
        // tint while retries are still running.
        borderLeft: `4px solid ${row.stopped ? "var(--color-good-wine)" : "var(--color-good-wine-3)"}`,
        borderRadius: "var(--radius-card, 8px)",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        bgcolor: "var(--color-white)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, flex: 1 }}>{row.campaignTitle}</Typography>
        {row.stopped ? (
          <Chip size="small" color="error" label="Funding suspended" />
        ) : (
          <Chip
            size="small"
            label={`Attempt ${row.attempts}`}
            sx={{
              bgcolor: "var(--color-good-wine-bg)",
              color: "var(--color-good-wine)",
              border: "1px solid var(--color-good-wine-4)",
            }}
          />
        )}
      </Box>

      <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
        {row.groupName ?? "—"} · {fmt(row.amountOwed)} owed · {fmt(row.coverage)} of coverage left
      </Typography>

      {/* The server renders the sentence, so staff and the client read the same words. */}
      <Typography sx={{ fontSize: 13 }}>{row.reason}</Typography>

      <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
        {row.lastAttemptAt ? `Last tried ${formatDateTime(row.lastAttemptAt)}. ` : ""}
        {row.stopped
          ? "Nothing further will be tried until a working card is nominated or someone retries."
          : row.nextAttemptAt
            ? `Next attempt ${formatDateTime(row.nextAttemptAt)}.`
            : "Another attempt is due shortly."}
      </Typography>

      {result && (
        <Alert severity={result.startsWith("Charged") ? "success" : "info"} sx={{ fontSize: 13 }}>
          {result}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1 }}>
        {/* ONLY WITHIN THE CLIENT'S OWN ORGANISATION (Jayanth, 2026-09-14). Retrying a charge is
            GFG collecting money already owed; choosing which of a client's cards gets charged is
            not GFG's to decide, and offering it here would put another organisation's payment
            instrument one click from a staff screen. For a client's own people it is the primary
            action — for an SCA decline or an expired card, retrying the same card cannot work. */}
        {ownOrganisation && (
          <Button
            size="small"
            variant={row.failureClass === "sca" ? "contained" : "outlined"}
            onClick={() => navigate(`/campaigns/${row.campaignId}/payment-card`)}
          >
            Change card
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={onRetry}
          // An SCA decline cannot be cleared by retrying: the bank wants the cardholder, and an
          // unattended charge will fail the same way. Offering the button would be a lie.
          disabled={retry.isPending || !row.hasPaymentMethod || row.failureClass === "sca"}
        >
          {retry.isPending ? "Retrying…" : "Retry now"}
        </Button>
      </Box>
    </Box>
  );
}

export default function FundingAttentionStrip() {
  const { data, isLoading } = useTopupsNeedingAttention();

  if (isLoading) return <CircularProgress size={18} sx={{ mb: 2 }} />;
  // It lives in a tab now, so empty has to SAY empty: a blank panel reads as "failed to load".
  if (!data || data.length === 0) {
    return (
      <Typography sx={{ fontSize: 14, color: "var(--color-graphite)" }}>
        No campaigns are having trouble collecting.
      </Typography>
    );
  }

  const stopped = data.filter((r) => r.stopped).length;

  return (
    <Box sx={{ mb: 4, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Funding needs attention</Typography>
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
          {stopped > 0
            ? `${stopped} campaign${stopped === 1 ? "" : "s"} with funding suspended`
            : `${data.length} campaign${data.length === 1 ? "" : "s"} awaiting a successful payment`}
        </Typography>
      </Box>
      {data.map((row) => (
        <AttentionRow key={row.campaignId} row={row} />
      ))}
    </Box>
  );
}
